// The game's front screens — splash, main menu, settings, name entry — over one shared full-bleed art
// layer. Rendering and input only: which screen comes next is screens.ts's call, made in main.ts.
// Same plain-innerHTML style as heroSelect.ts/lobbyView.ts, no framework.
import { GAME_NAME } from './gameName.js';
import { HEROES } from '../core/heroes.js';
import { escapeHtml, normalizeName, readName, writeName } from './nameStore.js';
import { readSettings, updateSettings, type Settings } from './settings.js';
import type { MenuItem } from './screens.js';
import { replayListHtml } from './replay/replayList.js';
import type { ListResult } from './replay/replayApi.js';
import { replayErrorText } from './replay/replayErrors.js';
import { getLang, setLang, t, type Lang } from './i18n/index.js';

declare const __BUILD_VERSION__: string | undefined;  // scripts/build.ts defines it; absent under bun test
export const BUILD_VERSION = typeof __BUILD_VERSION__ === 'string' ? __BUILD_VERSION__ : 'dev';

// Counted from HEROES, so a new class never leaves it stale.
const tagline = () => t('front.tagline', { n: Object.keys(HEROES).length });

/* Chrome fills a name-shaped field from the signed-in profile even with autocomplete=off — it only skips
   fields that are readonly at load time. Drop readonly on the first real focus so typing still works. */
function dodgeAutofill(el: HTMLInputElement) {
  el.addEventListener('focus', () => el.removeAttribute('readonly'), { once: true });
}

/* Each language named in itself, whatever the current one (marked with its own lang for screen readers). */
const LANG_OPTIONS: [Lang, string, Lang][] = [['ru', 'Русский', 'ru'], ['en', 'English', 'en']];

export type FrontHandle = {
  splash(opts: { onContinue(): void }): void;
  menu(opts: { onChoose(item: MenuItem): void; onBack(): void }): void;
  settings(opts: { onChange(s: Settings): void; onBack(): void }): void;
  nameEntry(opts: { onDone(name: string): void; onBack(): void }): void;
  notice(opts: { title: string; text: string; onBack(): void }): void;
  /** «Мои повторы»: «Загрузка…» until `load` answers, then the list, its empty state or the error (a replayApi code). */
  replays(opts: { load(): Promise<ListResult>; name: string | null; onOpen(id: string): void; onBack(): void }): void;
  /** A wait with a way out, e.g. while a replay downloads. */
  loading(opts: { title: string; onBack(): void }): void;
  hide(): void;
};

export function mountFront(root: HTMLElement): FrontHandle {
  root.innerHTML = `<div class="front-art" aria-hidden="true"></div><div class="front-content" id="frontContent"></div>`;
  const content = root.querySelector('#frontContent') as HTMLElement;
  let cleanup: (() => void) | null = null;

  function show(screen: string, html: string) {
    cleanup?.(); cleanup = null;
    root.hidden = false;
    root.dataset.screen = screen;
    content.innerHTML = html;
  }
  function listen<K extends keyof WindowEventMap>(type: K, fn: (e: WindowEventMap[K]) => void) {
    window.addEventListener(type, fn);
    const prev = cleanup;
    cleanup = () => { prev?.(); window.removeEventListener(type, fn); };
  }
  function onEscape(fn: () => void) {
    // defaultPrevented: the Esc was already consumed — e.g. by the hero screen that just navigated here
    listen('keydown', e => { if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); fn(); } });
  }

  return {
    splash({ onContinue }) {
      show('splash', `
        <div class="splash">
          <div class="splash-head">
            <h1 class="front-logo">${GAME_NAME}</h1>
            <p class="front-tag">${tagline()}</p>
          </div>
          <p class="splash-press">${t('front.pressAnyKey')}</p>
        </div>`);
      // keyup / click, not keydown / pointerdown: the menu that replaces this screen must not receive the
      // rest of the same gesture (Space activates a focused button on keyup; a click lands where it ends).
      // Only a key pressed here counts: the keyup of the Esc that brought the player back from the menu must
      // not bounce them straight into it again.
      let done = false, armed = false;
      const go = () => { if (!done) { done = true; onContinue(); } };
      listen('keydown', e => { if (!['Shift', 'Control', 'Alt', 'Meta', 'Tab'].includes(e.key)) armed = true; });
      listen('keyup', () => { if (armed) go(); });
      listen('click', go);
    },

    menu({ onChoose, onBack }) {
      const name = readName()?.trim();
      const items: [MenuItem, string][] = [
        ['online', t('menu.online')], ['bots', t('menu.bots')], ['editor', t('menu.editor')], ['replays', t('menu.replays')], ['heroes', t('menu.heroes')], ['settings', t('menu.settings')],
      ];
      show('menu', `
        <nav class="menu-plate" aria-label="${t('menu.aria')}">
          <h1 class="front-logo">${GAME_NAME}</h1>
          <p class="front-tag">${tagline()}</p>
          <ul class="menu-list">${items.map(([id, label]) =>
            `<li><button type="button" class="menu-item" data-item="${id}">${label}</button></li>`).join('')}</ul>
          <p class="menu-who">${name ? t('menu.you', { name: `<b>${escapeHtml(name)}</b>` }) : t('menu.noName')}</p>
        </nav>
        <p class="front-version">${escapeHtml(BUILD_VERSION)}</p>`);
      const buttons = [...content.querySelectorAll<HTMLButtonElement>('.menu-item')];
      buttons.forEach(b => b.addEventListener('click', () => onChoose(b.dataset.item as MenuItem)));
      buttons.forEach(b => b.addEventListener('mouseenter', () => b.focus({ preventScroll: true })));
      listen('keydown', e => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
        const step = e.key === 'ArrowDown' ? 1 : -1;
        buttons[at < 0 ? 0 : (at + step + buttons.length) % buttons.length]!.focus();
      });
      onEscape(onBack);
      buttons[0]!.focus({ preventScroll: true });
    },

    settings({ onChange, onBack }) {
      const s = readSettings();
      const radios = (key: keyof Settings, options: [string, string, Lang?][], current: string) =>
        `<div class="set-seg" role="radiogroup" aria-labelledby="setLabel-${key}">${options.map(([v, label, lang]) => `
          <label><input type="radio" name="${key}" value="${v}"${v === current ? ' checked' : ''}><span${lang ? ` lang="${lang}"` : ''}>${label}</span></label>`).join('')}
        </div>`;
      show('settings', `
        <section class="front-panel settings" aria-label="${t('settings.title')}">
          <h2>${t('settings.title')}</h2>
          <div class="set-row">
            <label class="set-label" for="setName">${t('settings.name')}</label>
            <input id="setName" class="front-input" type="text" maxlength="24" placeholder="Deniska" autocomplete="off" readonly
                   value="${escapeHtml(readName() ?? '')}">
          </div>
          <div class="set-row">
            <span class="set-label" id="setLabel-lang">${t('settings.lang')}</span>
            ${radios('lang', LANG_OPTIONS, getLang())}
          </div>
          <div class="set-row">
            <span class="set-label" id="setLabel-sound">${t('settings.sound')}</span>
            <div class="set-sound">
              ${radios('sound', [['on', t('settings.on')], ['off', t('settings.off')]], s.sound ? 'on' : 'off')}
              <input id="setVolume" class="set-volume" type="range" min="0" max="100" step="5" value="${Math.round(s.volume * 100)}"
                     aria-label="${t('settings.volume')}">
            </div>
          </div>
          <div class="set-row">
            <span class="set-label" id="setLabel-quality">${t('settings.quality')}</span>
            ${radios('quality', [['auto', t('settings.quality.auto')], ['high', t('settings.quality.high')], ['medium', t('settings.quality.medium')], ['low', t('settings.quality.low')]], s.quality)}
          </div>
          <div class="set-row">
            <span class="set-label" id="setLabel-style">${t('settings.style')}</span>
            ${radios('style', [['engrave', t('settings.style.engrave')], ['toon', t('settings.style.toon')]], s.style)}
          </div>
          <div class="set-row">
            <span class="set-label" id="setLabel-fog">${t('settings.fog')}</span>
            ${radios('fog', [['color', t('settings.fog.color')], ['mono', t('settings.fog.mono')]], s.fog)}
          </div>
          <div class="set-row">
            <span class="set-label" id="setLabel-hints">${t('settings.hints')}</span>
            ${radios('hints', [['on', t('settings.hints.show')], ['off', t('settings.hints.hide')]], s.hints ? 'on' : 'off')}
          </div>
          <div class="set-row">
            <span class="set-label" id="setLabel-lockCursor">${t('settings.lockCursor')}</span>
            ${radios('lockCursor', [['on', t('settings.on')], ['off', t('settings.off')]], s.lockCursor ? 'on' : 'off')}
          </div>
          <button type="button" class="front-btn" id="setBack">${t('common.back')}</button>
        </section>`);
      const nameEl = content.querySelector('#setName') as HTMLInputElement;
      dodgeAutofill(nameEl);
      // An emptied field clears the name (online then asks for one) instead of storing the fallback.
      nameEl.addEventListener('input', () => writeName(nameEl.value.trim() ? normalizeName(nameEl.value) : ''));
      const volumeEl = content.querySelector('#setVolume') as HTMLInputElement;
      volumeEl.addEventListener('input', () => onChange(updateSettings({ volume: Number(volumeEl.value) / 100 })));
      content.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach(r => r.addEventListener('change', () => {
        const v = r.value;
        if (r.name === 'lang') { onChange(updateSettings({ lang: v as Lang })); setLang(v as Lang); return; }
        const patch: Partial<Settings> =
          r.name === 'sound' ? { sound: v === 'on' } : r.name === 'hints' ? { hints: v === 'on' }
          : r.name === 'lockCursor' ? { lockCursor: v === 'on' }
          : r.name === 'quality' ? { quality: v as Settings['quality'] }
          : r.name === 'style' ? { style: v as Settings['style'] } : { fog: v as Settings['fog'] };
        onChange(updateSettings(patch));
      }));
      content.querySelector('#setBack')!.addEventListener('click', onBack);
      onEscape(onBack);
      (content.querySelector('input[type="radio"]:checked') as HTMLInputElement | null)?.focus({ preventScroll: true });
    },

    nameEntry({ onDone, onBack }) {
      show('name', `
        <section class="front-panel name-entry" aria-label="${t('settings.name')}">
          <h2>${t('nameEntry.title')}</h2>
          <p class="front-note">${t('nameEntry.note')}</p>
          <input id="nameInput" class="front-input" type="text" maxlength="24" placeholder="Deniska" autocomplete="off" readonly
                 value="${escapeHtml(readName() ?? '')}">
          <p class="front-error" id="nameError" hidden>${t('nameEntry.empty')}</p>
          <div class="front-actions">
            <button type="button" class="front-btn" id="nameBack">${t('common.back')}</button>
            <button type="button" class="front-btn front-btn-primary" id="nameGo">${t('common.next')}</button>
          </div>
        </section>`);
      const input = content.querySelector('#nameInput') as HTMLInputElement;
      const error = content.querySelector('#nameError') as HTMLElement;
      dodgeAutofill(input);
      const submit = () => {
        if (!input.value.trim()) { error.hidden = false; input.focus(); return; }
        const name = normalizeName(input.value);
        writeName(name);
        onDone(name);
      };
      input.addEventListener('keydown', e => { if (e.key === 'Enter') submit(); });
      input.addEventListener('input', () => { error.hidden = true; });
      content.querySelector('#nameGo')!.addEventListener('click', submit);
      content.querySelector('#nameBack')!.addEventListener('click', onBack);
      onEscape(onBack);
      input.focus({ preventScroll: true });
    },

    notice({ title, text, onBack }) {
      show('notice', `
        <section class="front-panel" role="alert">
          <h2>${escapeHtml(title)}</h2>
          <p class="front-note">${escapeHtml(text)}</p>
          <button type="button" class="front-btn" id="noticeBack">${t('common.back')}</button>
        </section>`);
      const btn = content.querySelector('#noticeBack') as HTMLButtonElement;
      btn.addEventListener('click', onBack);
      onEscape(onBack);
      btn.focus({ preventScroll: true });
    },

    replays({ load, name, onOpen, onBack }) {
      show('replays', `
        <section class="front-panel replays" aria-label="${t('menu.replays')}">
          <h2>${t('menu.replays')}</h2>
          <div class="rl-body" id="rlBody" aria-live="polite"><p class="front-note">${t('common.loading')}</p></div>
          <button type="button" class="front-btn" id="rlBack">${t('common.back')}</button>
        </section>`);
      const body = content.querySelector('#rlBody') as HTMLElement;
      const backBtn = content.querySelector('#rlBack') as HTMLButtonElement;
      backBtn.addEventListener('click', onBack);
      onEscape(onBack);
      backBtn.focus({ preventScroll: true });
      // A late answer must not land on whatever screen replaced this one.
      const shownAt = cleanup;
      void load().then(r => {
        if (cleanup !== shownAt || root.dataset.screen !== 'replays') return;
        body.innerHTML = r.ok ? replayListHtml(r.replays, name) : `<p class="front-error" role="alert">${escapeHtml(replayErrorText(r.error))}</p>`;
        body.querySelectorAll<HTMLButtonElement>('[data-replay]').forEach(b => b.addEventListener('click', () => onOpen(b.dataset.replay!)));
        body.querySelector<HTMLButtonElement>('[data-replay]')?.focus({ preventScroll: true });
      });
    },

    loading({ title, onBack }) {
      show('loading', `
        <section class="front-panel" aria-busy="true">
          <h2>${escapeHtml(title)}</h2>
          <p class="front-note">${t('common.loading')}</p>
          <button type="button" class="front-btn" id="loadingBack">${t('common.back')}</button>
        </section>`);
      const btn = content.querySelector('#loadingBack') as HTMLButtonElement;
      btn.addEventListener('click', onBack);
      onEscape(onBack);
      btn.focus({ preventScroll: true });
    },

    hide() {
      cleanup?.(); cleanup = null;
      content.innerHTML = '';
      root.hidden = true;
    },
  };
}
